import { CommonModule, isPlatformServer } from '@angular/common';
import {
	Component,
	computed,
	effect,
	inject,
	input,
	PLATFORM_ID,
	signal,
	untracked,
} from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';
import { DynamicModule } from 'ng-dynamic-component';

import { MapComponent } from '@valk-nx/components/ui-map/src/lib/map';
import type {
	MapBounds,
	MapPinpoint,
} from '@valk-nx/components/ui-map/src/lib/map.interface';
import {
	boundsOf,
	type MapView,
} from '@valk-nx/components/ui-map/src/lib/map.view';
import { PanelComponent } from '@valk-nx/components/ui-panel/src/lib/panel';
import { SocialProofComponent } from '@valk-nx/components/ui-social-proof/src/lib/social-proof';
import {
	DEFAULT_SORT_OPTIONS,
	FilterHeadingComponent,
} from '@valk-nx/compositions/ui-filters/src/lib/components/filter-heading/filter-heading';
import { FilterSheetButtonComponent } from '@valk-nx/compositions/ui-filters/src/lib/components/filter-sheet-button/filter-sheet-button';
import type {
	FiltersInterface,
	SortSelection,
} from '@valk-nx/compositions/ui-filters/src/lib/contracts/filters.interface';
import { FiltersComponent } from '@valk-nx/compositions/ui-filters/src/lib/filters.component';
import { ImageCardComponent } from '@valk-nx/compositions/ui-image-card/src/lib/image-card.component';
import type { PhotoInterface } from '@valk-nx/core/lib/interfaces/image.interface';
import { RouteHelper } from '@valk-nx/router-store/router.helper';
import { injectQueryParamsMap } from '@valk-nx/services/query-params';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { extractCarouselImages } from '@valk-nx/storyblok-helpers/src/lib/general/carousel-images.helper';
import { preselectedFilters } from '@valk-nx/storyblok-services/src/lib/services/filterable-list.filters';
import { FilterableListService } from '@valk-nx/storyblok-services/src/lib/services/filterable-list.service';
import { MetadataService } from '@valk-nx/storyblok-services/src/lib/services/metadata.service';
import type {
	FilterableHotel,
	FilterableHotelListData,
	FilterableListItems,
	FilterableListScenario,
	FilterablePackageBatch,
	FilterablePackageBatchListData,
	FilterableRoom,
	FilterableRoomListData,
	TransformedFilterableMapHotel,
} from '@valk-nx/storyblok-types/src/lib/types/filterable-list.service.interface';
import type {
	HeadingGroupSection,
	ISbStoryBlok,
} from '@valk-nx/storyblok-types/src/lib/types/storyblok.interface';

import { VdcHotels } from './lists/vdc-hotels/vdc-hotels';
import { VdcPackages } from './lists/vdc-packages/vdc-packages';
import { VdcRooms } from './lists/vdc-rooms/vdc-rooms';
import { VdcvhiHotels } from './lists/vdcvhi-hotels/vdcvhi-hotels';
import type { DynamicContentInputs } from './filterable-list.dynamic-content';
import {
	carouselComponents,
	richTextComponents,
	subContentComponents,
	titleComponents,
	withComponentName,
} from './filterable-list.dynamic-content';
import {
	toBaseUrl,
	toCardUrl,
	toFiltersInterface,
} from './filterable-list.helpers';
import type {
	FilterableListHotel,
	FilterableListType,
} from './filterable-list.interface';
import { LocationSearchFilter } from './location-search-filter';
import { injectMapFragment } from './map-fragment';

const scenarioMap = {
	vdc: {
		hotels: 'vdc-hotels',
		packages: 'vdc-packages',
		events: 'vdc-events',
		rooms: 'vdc-rooms',
	},
	vdcvhi: {
		hotels: 'vdcvhi-hotels',
		packages: 'vdcvhi-packages',
		events: 'vdcvhi-events',
		rooms: 'vdcvhi-rooms',
	},
} as const satisfies Record<
	FilterableListHotel,
	Record<FilterableListType, FilterableListScenario>
>;

const DEFAULT_MAP_VIEW: MapView = { bounds: [2.4, 49.2, 9.14, 53.4] };

const INITIAL_ZOOM = 11;

// TODO: VPL-49088 - Make the countries and bounding box configurable or fetch from an API
const COUNTRY_BOUNDS: Partial<Record<string, MapBounds>> = {
	nl: [3.36, 50.75, 7.23, 53.56],
	be: [2.54, 49.5, 6.41, 51.51],
	de: [5.87, 47.27, 15.04, 55.06],
	fr: [-5.14, 41.33, 9.56, 51.09],
	es: [-9.3, 35.95, 3.32, 43.79],
	cw: [-69.16, 12.03, -68.64, 12.39],
	bq: [-68.42, 12.02, -68.19, 12.32],
};

@Component({
	providers: [LocationSearchFilter],
	selector: 'sb-filterable-list',
	templateUrl: './filterable-list.html',
	imports: [
		CommonModule,
		DynamicModule,
		FiltersComponent,
		FilterHeadingComponent,
		FilterSheetButtonComponent,
		ImageCardComponent,
		MapComponent,
		PanelComponent,
		SocialProofComponent,
		TranslatePipe,
		VdcHotels,
		VdcPackages,
		VdcRooms,
		VdcvhiHotels,
	],
})
export class FilterableList extends StoryblokRootDirective {
	protected readonly filterableListService = inject(FilterableListService);
	protected readonly locationSearch = inject(LocationSearchFilter);
	private readonly urlFilterParams = injectQueryParamsMap();
	private readonly metadataService = inject(MetadataService);
	private readonly platformId = inject(PLATFORM_ID);

	hotel = input.required<FilterableListHotel>();
	type = input.required<FilterableListType>();
	carousel = input.required<ISbStoryBlok[]>();
	title = input.required<HeadingGroupSection[]>();
	content = input.required<ISbStoryBlok[]>();
	subContent = input<ISbStoryBlok[]>();
	listItems = input<FilterableListItems>({});
	showPrices = input<boolean>(true);
	baseUrl = input('', { transform: toBaseUrl });

	sortOption = signal<SortSelection>('none');
	protected readonly mapFragment = injectMapFragment();

	activeFilterCount = computed(() => {
		const filterCount = Object.values(
			this.filterableListService.filters(),
		).reduce((sum, values) => sum + values.length, 0);
		const locationCount = this.filterableListService.location() ? 1 : 0;

		return filterCount + locationCount;
	});

	valkBusiness = computed<boolean>(() => !!this.listItems()?.valkBusiness);

	activeScenario = computed<FilterableListScenario | null>(() => {
		const hotel = this.hotel();
		const type = this.type();

		if (!hotel || !type) return null;

		return scenarioMap[hotel]?.[type] ?? null;
	});

	private readonly activeListData = computed<
		| FilterableHotelListData
		| FilterablePackageBatchListData
		| FilterableRoomListData
		| undefined
	>(() => {
		const scenario = this.activeScenario();
		if (scenario === 'vdc-hotels') {
			return this.filterableListService.vdcHotels.value();
		}
		if (scenario === 'vdc-packages') {
			return this.filterableListService.vdcPackages.value();
		}
		if (scenario === 'vdcvhi-hotels') {
			return this.filterableListService.vdcvhiHotels.value();
		}
		if (scenario === 'vdc-rooms') {
			return this.filterableListService.vdcRooms.value();
		}
		return undefined;
	});

	private readonly hotelList = computed<FilterableHotel[]>(() => {
		const data = this.activeListData();
		return data && 'hotels' in data ? data.hotels : [];
	});

	private readonly packageBatchList = computed<FilterablePackageBatch[]>(
		() => {
			const data = this.activeListData();
			return data && 'packageBatches' in data ? data.packageBatches : [];
		},
	);

	private readonly roomList = computed<FilterableRoom[]>(() => {
		const data = this.activeListData();
		return data && 'rooms' in data ? data.rooms : [];
	});

	private readonly onlyCountryFilterActive = computed(() => {
		const filters = this.filterableListService.filters();
		return Object.entries(filters).every(([key]) => key === 'countries');
	});

	numberOfResults = computed(
		() =>
			this.hotelList().length +
			this.packageBatchList().length +
			this.roomList().length,
	);

	mapHotels = computed<TransformedFilterableMapHotel[]>(() =>
		(this.activeListData()?.mapHotels ?? []).map((hotel) => ({
			...hotel,
			cardUrl: toCardUrl(hotel, this.baseUrl()),
		})),
	);

	mapPinpoints = computed<MapPinpoint[]>(() =>
		this.mapHotels().map((hotel) => ({
			lat: hotel.geoLocation.latitude,
			long: hotel.geoLocation.longitude,
			active: hotel.activeOnMap,
			label: hotel.name,
		})),
	);

	mapView = computed<MapView | undefined>(() => {
		if (this.activeListData() && this.numberOfResults() === 0) {
			return DEFAULT_MAP_VIEW;
		}
		const location = this.filterableListService.location();
		if (location) {
			return {
				center: [location.longitude, location.latitude],
				zoom: INITIAL_ZOOM,
			};
		}
		if (this.onlyCountryFilterActive()) {
			const countryBounds = (
				this.filterableListService.filters()['countries'] ?? []
			)
				.map((code) => COUNTRY_BOUNDS[code.toLocaleLowerCase()])
				.filter((bounds) => bounds !== undefined);
			if (countryBounds.length) {
				return {
					bounds: boundsOf(
						countryBounds.flatMap(([west, south, east, north]) => [
							[west, south],
							[east, north],
						]),
					),
				};
			}
		}
		return this.activeFilterCount() ? undefined : DEFAULT_MAP_VIEW;
	});

	openHotel(url: string): void {
		RouteHelper.redirectToExternalUrlInNewTab(url);
	}

	filters = computed<FiltersInterface[]>(() => {
		const data = this.activeListData();
		return data ? toFiltersInterface(data.filters) : [];
	});

	sortOptions = computed(() => {
		// VHI hotels only support sorting by name (A-Z / Z-A)
		if (!this.showPrices()) {
			return DEFAULT_SORT_OPTIONS.filter(
				(option) => !option.value.startsWith('price'),
			);
		}

		return DEFAULT_SORT_OPTIONS;
	});

	shouldShowCarousel = computed(() => this.carousel().length > 0);

	carouselImages = computed<PhotoInterface[]>(() =>
		extractCarouselImages(this.carousel()),
	);

	carouselInputs = computed<DynamicContentInputs[]>(() =>
		withComponentName(this.carousel()),
	);

	contentInputs = computed<DynamicContentInputs[]>(() =>
		withComponentName(this.content(), { isReadMore: true }),
	);

	subContentInput = computed<DynamicContentInputs[]>(() =>
		withComponentName(this.subContent() ?? []),
	);

	carouselComponents: typeof carouselComponents = carouselComponents;
	titleComponents: typeof titleComponents = titleComponents;
	richTextComponents: typeof richTextComponents = richTextComponents;
	subContentComponents: typeof subContentComponents = subContentComponents;

	constructor() {
		super();

		effect(() => {
			this.metadataService.updateMetaDataBatch(
				{
					meta: {
						title: this.metaTitle(),
						description: this.metaDescription(),
					},
					images: this.carouselImages(),
				},
				this.robotsNoFollow(),
				this.robotsNoIndex(),
			);
		});

		effect(() => {
			// Seed from the story uid so a new page starts fresh.
			const seedKey = {
				uid: this._uid(),
				scenario: this.activeScenario(),
			};
			this.filterableListService.scenario.set(
				seedKey.scenario ?? undefined,
			);
			this.filterableListService.location.set(null);
			// Plugin preselection first, URL params win. Untracked so later
			// URL changes can't overwrite the visitor's selections.
			this.filterableListService.filters.set({
				...preselectedFilters(untracked(this.listItems)),
				...untracked(this.urlFilterParams),
			});
		});

		effect(() => {
			this.filterableListService.listItems.set(this.listItems());
		});

		effect(() => {
			if (!isPlatformServer(this.platformId)) return;
			const hotels = this.hotelList();
			if (!hotels.length) return;
			untracked(() => {
				this.metadataService.setSchemaOrgHotelList(
					this.metaTitle(),
					this.metaDescription(),
					hotels,
				);
			});
		});

		effect(() => {
			if (!isPlatformServer(this.platformId)) return;
			if (this.activeScenario() !== 'vdc-packages') return;
			const packageBatches = this.packageBatchList();
			if (!packageBatches.length) return;
			untracked(() => {
				this.metadataService.setSchemaOrgFilterablePackageList(
					this.metaTitle(),
					this.metaDescription(),
					this.carouselImages(),
					packageBatches,
				);
			});
		});
	}
}
